<template>
  <div class="px-3 py-3">
    <div class="card">
      <div class="card-body">
        <div class="d-flex align-items-center mb-2">
          <h5 class="mb-0 mr-3 font-weight-bold">
            {{ $t("lang.label.cold_call_script.title") }}
          </h5>
          <span
            v-if="isManualColdCallScriptActive"
            class="badge badge-success text-white px-2 py-1"
          >
            {{ $t("lang.label.cold_call_script.manual_active") }}
          </span>
          <span v-else class="badge badge-secondary text-white px-2 py-1">
            {{ $t("lang.label.cold_call_script.ai_default") }}
          </span>
        </div>

        <!-- Clientes: los 7 campos son notas internas, no se muestran -->
        <p v-if="!isInternalUser" class="text-muted mb-0">
          {{ $t("lang.label.cold_call_script.placeholder_no_admin_billing") }}
        </p>

        <template v-else>
          <p class="text-muted mb-3">
            {{ $t("lang.label.cold_call_script.description_v2") }}
          </p>

          <div v-if="loading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </div>

          <template v-else>
            <div class="d-flex align-items-center mb-3">
              <div class="progress flex-grow-1 mr-3" style="height: 6px; max-width: 240px">
                <div
                  class="progress-bar bg-success"
                  role="progressbar"
                  :style="{ width: (filledCount / 7) * 100 + '%' }"
                ></div>
              </div>
              <small class="text-muted">
                {{ $t("lang.label.cold_call_script.fields_filled", { n: filledCount }) }}
              </small>
            </div>

            <!-- 6 campos de texto -->
            <div
              v-for="(field, i) in textFields"
              :key="field.key"
              class="row border-top py-3"
            >
              <div class="col-md-4 mb-2">
                <div class="font-weight-bold">
                  <span class="badge badge-light mr-1">{{ i + 1 }}</span>
                  {{ $t("lang.label.cold_call_script." + field.label) }}
                </div>
                <small class="text-muted d-block mb-1">
                  {{ $t("lang.label.cold_call_script." + field.label + "_help") }}
                </small>
                <span class="badge badge-light mr-1">
                  {{ $t("lang.label.cold_call_script." + field.leadroom) }}
                </span>
                <span class="badge" :class="field.ai ? 'badge-info text-white' : 'badge-light text-success'">
                  {{ $t("lang.label.cold_call_script." + (field.ai ? "tag_ai" : "tag_raw")) }}
                </span>
              </div>
              <div class="col-md-8">
                <textarea
                  :id="'cc-' + field.key"
                  class="form-control"
                  :class="{ 'is-invalid': errors[field.key] }"
                  :rows="field.rows"
                  :maxlength="field.limit"
                  :placeholder="field.placeholder ? $t('lang.label.cold_call_script.' + field.placeholder) : ''"
                  v-model="draft[field.key]"
                  :disabled="!canEdit"
                ></textarea>
                <div v-if="errors[field.key]" class="invalid-feedback d-block">
                  {{ errors[field.key] }}
                </div>
                <small class="d-block text-right text-muted mt-1">
                  {{ draft[field.key].length }} / {{ field.limit }}
                  {{ $t("lang.label.cold_call_script.characters") }}
                </small>
              </div>
            </div>

            <!-- 7. Objeciones -->
            <div class="row border-top py-3">
              <div class="col-md-4 mb-2">
                <div class="font-weight-bold">
                  <span class="badge badge-light mr-1">7</span>
                  {{ $t("lang.label.cold_call_script.objections") }}
                </div>
                <small class="text-muted d-block mb-1">
                  {{ $t("lang.label.cold_call_script.objections_help") }}
                </small>
                <span class="badge badge-light mr-1">
                  {{ $t("lang.label.cold_call_script.leadroom_tabs") }}
                </span>
                <span class="badge badge-light text-success">
                  {{ $t("lang.label.cold_call_script.tag_raw") }}
                </span>
              </div>
              <div class="col-md-8">
                <div
                  v-for="(objection, i) in draft.cc_objections"
                  :key="objection.uid"
                  class="border rounded p-2 mb-2"
                >
                  <div class="d-flex align-items-start mb-1">
                    <input
                      :id="'cc-objection-titre-' + objection.uid"
                      type="text"
                      class="form-control form-control-sm font-weight-bold"
                      :maxlength="objectionLimits.titre"
                      :placeholder="$t('lang.label.cold_call_script.objection_title_placeholder')"
                      v-model="objection.titre"
                      :disabled="!canEdit"
                    />
                    <button
                      v-if="canEdit"
                      type="button"
                      class="btn btn-sm btn-link text-danger ml-1"
                      :title="$t('lang.label.cold_call_script.objection_remove')"
                      :aria-label="$t('lang.label.cold_call_script.objection_remove')"
                      @click="removeObjection(i)"
                    >
                      &times;
                    </button>
                  </div>
                  <small class="d-block text-right text-muted mb-1">
                    {{ objection.titre.length }} / {{ objectionLimits.titre }}
                  </small>
                  <input
                    :id="'cc-objection-consigne-' + objection.uid"
                    type="text"
                    class="form-control form-control-sm"
                    :maxlength="objectionLimits.consigne"
                    :placeholder="$t('lang.label.cold_call_script.objection_rule')"
                    v-model="objection.consigne"
                    :disabled="!canEdit"
                  />
                  <small class="d-block text-right text-muted mb-1">
                    {{ objection.consigne.length }} / {{ objectionLimits.consigne }}
                  </small>
                  <textarea
                    :id="'cc-objection-reponse-' + objection.uid"
                    class="form-control form-control-sm"
                    rows="4"
                    :maxlength="objectionLimits.reponse"
                    :placeholder="$t('lang.label.cold_call_script.objection_answer')"
                    v-model="objection.reponse"
                    :disabled="!canEdit"
                  ></textarea>
                  <small class="d-block text-right text-muted">
                    {{ objection.reponse.length }} / {{ objectionLimits.reponse }}
                  </small>
                  <div v-if="objectionErrors[i]" class="invalid-feedback d-block">
                    {{ objectionErrors[i] }}
                  </div>
                </div>
                <div v-if="errors.cc_objections" class="invalid-feedback d-block mb-2">
                  {{ errors.cc_objections }}
                </div>
                <button
                  v-if="canEdit"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="draft.cc_objections.length >= maxObjections"
                  @click="addObjection()"
                >
                  {{ $t("lang.label.cold_call_script.objection_add", { n: draft.cc_objections.length }) }}
                </button>
              </div>
            </div>

            <!-- Texto viejo: solo lectura, sigue siendo el repli del Pitch -->
            <div v-if="legacyScript" class="border-top pt-3">
              <button
                type="button"
                class="btn btn-link p-0 text-left"
                :aria-expanded="showLegacy ? 'true' : 'false'"
                @click="showLegacy = !showLegacy"
              >
                {{ showLegacy ? "▾" : "▸" }}
                {{ $t("lang.label.cold_call_script.legacy_title") }}
              </button>
              <textarea
                v-if="showLegacy"
                id="cc-legacy"
                class="form-control mt-2"
                rows="12"
                :value="legacyScript"
                readonly
              ></textarea>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
              <small v-if="hasChanges" class="text-warning font-weight-bold">
                {{ $t("lang.label.cold_call_script.unsaved_changes") }}
              </small>
              <span v-else></span>
              <button
                class="btn btn-primary px-4"
                type="button"
                :disabled="!canEdit || !hasChanges || saving"
                @click="save()"
              >
                <span
                  v-if="saving"
                  class="spinner-border spinner-border-sm mr-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ $t("lang.button.save") }}
              </button>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { targetsService } from "@/_services";

const TEXT_FIELDS = [
  { key: "cc_presentation", label: "presentation", limit: 1000, rows: 4, leadroom: "leadroom_closed", ai: false },
  { key: "cc_campagne", label: "campagne", limit: 800, rows: 4, leadroom: "leadroom_closed", ai: false },
  { key: "cc_lexique", label: "lexique", limit: 1500, rows: 6, leadroom: "leadroom_closed", ai: false, placeholder: "lexique_placeholder" },
  { key: "cc_rdv", label: "rdv", limit: 500, rows: 3, leadroom: "leadroom_closed", ai: false },
  { key: "cc_consignes", label: "consignes", limit: 600, rows: 4, leadroom: "leadroom_always", ai: false },
  { key: "cc_pitch", label: "pitch", limit: 1500, rows: 8, leadroom: "leadroom_open", ai: true },
];
const OBJECTION_LIMITS = { titre: 70, consigne: 150, reponse: 600 };
const MAX_OBJECTIONS = 5;

let objectionUid = 0;

function emptyDraft() {
  const draft = { cc_objections: [] };
  TEXT_FIELDS.forEach((f) => {
    draft[f.key] = "";
  });
  return draft;
}

// Mismo criterio que el servidor: \r\n -> \n y trim; vacio = null.
function normalize(value) {
  const v = (value || "").replace(/\r\n/g, "\n").trim();
  return v === "" ? null : v;
}

function buildPayload(draft) {
  const payload = {};
  TEXT_FIELDS.forEach((f) => {
    payload[f.key] = normalize(draft[f.key]);
  });
  payload.cc_objections = draft.cc_objections.map((o) => ({
    titre: normalize(o.titre),
    consigne: normalize(o.consigne),
    reponse: normalize(o.reponse)
  }));
  return payload;
}

export default {
  name: "ColdCallScript",
  props: {
    targetId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      textFields: TEXT_FIELDS,
      objectionLimits: OBJECTION_LIMITS,
      maxObjections: MAX_OBJECTIONS,
      loading: false,
      saving: false,
      draft: emptyDraft(),
      savedPayload: JSON.stringify(buildPayload(emptyDraft())),
      legacyScript: "",
      showLegacy: false,
      errors: {},
      objectionErrors: {}
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.authentication.user
    }),
    isInternalUser() {
      return this.currentUser && this.currentUser.role_id == 1;
    },
    isUserAccountManager() {
      return (
        this.currentUser.role_id == 1 &&
        this.currentUser.user_constraint_id == 2
      );
    },
    canEdit() {
      return this.hasPermissions("superAdmin") || this.isUserAccountManager;
    },
    payload() {
      return buildPayload(this.draft);
    },
    hasChanges() {
      return JSON.stringify(this.payload) !== this.savedPayload;
    },
    filledCount() {
      let n = TEXT_FIELDS.filter((f) => normalize(this.draft[f.key]) !== null).length;
      if (this.payload.cc_objections.length) {
        n++;
      }
      return n;
    },
    isManualColdCallScriptActive() {
      return !!(normalize(this.legacyScript) || normalize(this.draft.cc_pitch));
    }
  },
  watch: {
    targetId: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    hasPermissions(perm) {
      if (!this.$store.getters["authentication/hasPermissions"]) {
        return false;
      }
      return this.$store.getters["authentication/hasPermissions"](perm);
    },
    onBeforeUnload(e) {
      if (this.hasChanges) {
        e.preventDefault();
        e.returnValue = this.$t("lang.label.cold_call_script.leave_unsaved");
        return e.returnValue;
      }
    },
    applyResponse(data) {
      const draft = emptyDraft();
      TEXT_FIELDS.forEach((f) => {
        draft[f.key] = data[f.key] || "";
      });
      draft.cc_objections = (data.cc_objections || []).map((o) => ({
        uid: ++objectionUid,
        titre: o.titre || "",
        consigne: o.consigne || "",
        reponse: o.reponse || ""
      }));
      this.draft = draft;
      this.savedPayload = JSON.stringify(buildPayload(draft));
      this.legacyScript = data.manual_cold_call_script || "";
      this.errors = {};
      this.objectionErrors = {};
    },
    load() {
      if (!this.isInternalUser || !this.targetId) {
        return;
      }
      this.loading = true;
      targetsService
        .getColdCallScript(this.targetId)
        .then((data) => this.applyResponse(data))
        .catch(() => {
          this.$swal({
            type: "error",
            title: this.$t("lang.label.error"),
            text: this.$t("lang.label.error"),
            showConfirmButton: true
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addObjection() {
      if (this.draft.cc_objections.length >= MAX_OBJECTIONS) {
        return;
      }
      this.draft.cc_objections.push({ uid: ++objectionUid, titre: "", consigne: "", reponse: "" });
    },
    removeObjection(i) {
      this.draft.cc_objections.splice(i, 1);
      this.objectionErrors = {};
    },
    save() {
      if (!this.canEdit || this.saving || !this.hasChanges) {
        return;
      }
      this.errors = {};
      this.objectionErrors = {};
      this.saving = true;
      targetsService
        .saveColdCallScript(this.targetId, this.payload)
        .then((data) => this.applyResponse(data))
        .catch((error) => {
          // 400: el mensaje va debajo del campo, sin perder lo escrito.
          if (error && error.status == 400 && error.field) {
            if (error.field === "cc_objections" && error.index !== undefined) {
              this.$set(this.objectionErrors, error.index, error.message);
            } else {
              this.$set(this.errors, error.field, error.message);
            }
            return;
          }
          this.$swal({
            type: "error",
            title: this.$t("lang.label.error"),
            text: (error && error.message) || this.$t("lang.label.error"),
            showConfirmButton: true
          });
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>
